import React from 'react';
import { Dialog } from 'primereact/dialog';
import { Checkbox } from 'primereact/checkbox';
import { useContext } from 'react';
import { LocaleContext } from '../LocaleContext';
import { NOTIFICATION_CHANNEL_OPTIONS, getLastOpenedNotificationType } from '../../utils/chosenNotifications';
import { getNotificationTopicDisplay } from '../../utils/notificationTopics';

/**
 * @param {'channels' | 'priorityDepartments'} [mode]
 * @param {{ id: number|string, name: string }[]} [departments] – pre mode priorityDepartments
 * @param {number[]} [departmentIds]
 * @param {function(number[]): void} [setDepartmentIds]
 * @param {string[]} [channelKeys] – 'in_app' | 'email' pre celý zoznam vybraných oddelení
 * @param {function(string[]): void} [setChannelKeys]
 */
export default function ChosenNotificationsDialog({
  visible,
  onHide,
  chosenNotifications = [],
  setChosenNotifications = () => {},
  onSave,
  mode = 'channels',
  departments = [],
  departmentIds = [],
  setDepartmentIds = () => {},
  channelKeys = [],
  setChannelKeys = () => {},
}) {
  const { t } = useContext(LocaleContext);
  const currentType = getLastOpenedNotificationType();
  const topicInfo = getNotificationTopicDisplay(currentType, t);
  const channelLabelByKey = {
    in_app: t('Common.NotificationChannel.InApp'),
    email: t('Common.NotificationChannel.Email'),
  };

  const toggleChannel = (channel, checked) => {
    const set = new Set(Array.isArray(chosenNotifications) ? chosenNotifications : []);
    if (checked) set.add(channel);
    else set.delete(channel);
    setChosenNotifications(Array.from(set));
  };

  const togglePriorityChannel = (channel, checked) => {
    const set = new Set(Array.isArray(channelKeys) ? channelKeys : []);
    if (checked) set.add(channel);
    else set.delete(channel);
    setChannelKeys?.(Array.from(set));
  };

  const selectedDeptIds = new Set(
    (Array.isArray(departmentIds) ? departmentIds : []).map((x) => Number(x)),
  );

  const toggleDepartment = (deptId, checked) => {
    const id = Number(deptId);
    const next = new Set(selectedDeptIds);
    if (checked) next.add(id);
    else next.delete(id);
    setDepartmentIds?.(Array.from(next));
  };

  if (mode === 'priorityDepartments') {
    return (
      <Dialog header={t('Common.SetNotifications')} visible={visible} style={{ width: '70vw' }} onHide={onHide}>
        <div className="flex flex-col">
          <div className="border border-gray-300 rounded-md p-2 m-1 bg-gray-50">
            <div className="text-sm font-semibold">{topicInfo.label}</div>
            <div className="text-xs text-gray-600 mt-1">
              {t('Common.SelectDepartmentsNotificationInfo')}
            </div>
          </div>
          {departments.map((item) => {
            const id = Number(item.id);
            const inputId = `prio-dept-${id}`;
            return (
              <div key={id} className="flex flex-row border border-black rounded-md p-2 m-1">
                <Checkbox
                  inputId={inputId}
                  checked={selectedDeptIds.has(id)}
                  onChange={(e) => toggleDepartment(id, !!e.checked)}
                />
                <label htmlFor={inputId} className="ml-2">{item.name}</label>
              </div>
            );
          })}
          <div className="border-t border-gray-200 mt-2 pt-2 text-sm font-medium text-gray-700 px-1">
            {t('Common.NotificationMethodForSelectedDepartments')}
          </div>
          {NOTIFICATION_CHANNEL_OPTIONS.map((option) => (
            <div key={option.key} className="flex flex-row border border-black rounded-md p-2 m-1">
              <Checkbox
                inputId={`prio-ch-${option.key}`}
                checked={Array.isArray(channelKeys) && channelKeys.includes(option.key)}
                onChange={(e) => togglePriorityChannel(option.key, !!e.checked)}
              />
              <label htmlFor={`prio-ch-${option.key}`} className="ml-2">{channelLabelByKey[option.key] || option.label}</label>
            </div>
          ))}
          <button
            type="button"
            onClick={onSave}
            className="flex justify-center items-center mt-4 w-full h-10 font-medium mb-2 px-2 py-1 tracking-wide text-white transition-colors duration-200 transform bg-zinc-900 rounded-md hover:bg-zinc-700 focus:outline-none focus:bg-zinc-600 disabled:opacity-25"
          >
            {t('Common.Save')}
          </button>
        </div>
      </Dialog>
    );
  }

  return (
    <Dialog header={t('Common.SetNotifications')} visible={visible} style={{ width: '70vw' }} onHide={onHide}>
      <div className="flex flex-col">
        <div className="border border-gray-300 rounded-md p-2 m-1 bg-gray-50">
          <div className="text-sm font-semibold">{topicInfo.label}</div>
        </div>
        {NOTIFICATION_CHANNEL_OPTIONS.map((option) => (
          <div key={option.key} className="flex flex-row border border-black rounded-md p-2 m-1">
            <Checkbox
              name={option.key}
              value={option.key}
              checked={Array.isArray(chosenNotifications) && chosenNotifications.includes(option.key)}
              onChange={(e) => toggleChannel(option.key, !!e.checked)}
            />
            <label htmlFor={option.key} className="ml-2">{channelLabelByKey[option.key] || option.label}</label>
          </div>
        ))}
        <button
          type="button"
          onClick={onSave}
          className="flex justify-center items-center mt-4 w-full h-10 font-medium mb-2 px-2 py-1 tracking-wide text-white transition-colors duration-200 transform bg-zinc-900 rounded-md hover:bg-zinc-700 focus:outline-none focus:bg-zinc-600 disabled:opacity-25"
        >
          {t('Common.Save')}
        </button>
      </div>
    </Dialog>
  );
}
